import { Job, JobLevel, JobLevelSpec } from '../../types/job';
import {
  GradeEnum,
  LevelModeEnum,
  SchoolLevelEnum,
  Sek1ModelEnum,
} from '../../types/rest-api';
import {
  GRADES,
  KIGA_GRADES,
  LEGACY_LEVEL_GRADES,
  QUARTAER_SPECS,
  SEK2_SPECS,
  TERTIAER_SPECS,
  ZYKLEN,
  ZyklusKey,
  gradesInRange,
  gradeNumber,
  labelToEducationLevel,
  zyklusByKey,
} from './level-catalog';
import { deriveLegacyMode } from './level-format';
import {
  SCHOOL_LEVEL_MAP,
  REVERSE_SCHOOL_LEVEL_MAP,
} from '../../types/enum-maps';

/** Editor state of the job «Stufen» form. Pure data — the component owns a signal of it. */
export interface EditorState {
  mode: LevelModeEnum;
  kiga: boolean;
  kigaGrades: GradeEnum[];
  primar: boolean;
  primarGrades: GradeEnum[];
  sek1: boolean;
  sek1Grades: GradeEnum[];
  sek2: boolean;
  sek2Specs: SchoolLevelEnum[];
  tertiaer: boolean;
  tertiaerSpecs: SchoolLevelEnum[];
  quartaer: boolean;
  quartaerSpecs: SchoolLevelEnum[];
  /** Shared by Stufen Sek I and Zyklus III. */
  sek1Models: Sek1ModelEnum[];
  zyklen: ZyklusKey[];
  zyklusGrades: GradeEnum[];
}

export const emptyEditorState: EditorState = {
  mode: 'STUFEN',
  kiga: false,
  kigaGrades: [],
  primar: false,
  primarGrades: [],
  sek1: false,
  sek1Grades: [],
  sek2: false,
  sek2Specs: [],
  tertiaer: false,
  tertiaerSpecs: [],
  quartaer: false,
  quartaerSpecs: [],
  sek1Models: [],
  zyklen: [],
  zyklusGrades: [],
};

const sortGrades = (g: GradeEnum[]) =>
  [...new Set(g)].sort((a, b) => GRADES.indexOf(a) - GRADES.indexOf(b));
const within = (g: GradeEnum[], range: GradeEnum[]) =>
  sortGrades(g.filter(x => range.includes(x)));
const specsIn = (specs: SchoolLevelEnum[], allowed: SchoolLevelEnum[]) =>
  specs.filter(s => allowed.includes(s));

export function editorStateFromJob(job: Job): EditorState {
  const levels = job.schoolLevels ?? [];
  const enums = levels
    .map(labelToEducationLevel)
    .filter((e): e is NonNullable<typeof e> => !!e);
  const allZyklus =
    enums.length > 0 && enums.every(e => e.startsWith('ZYKLUS_'));
  const anyZyklus = enums.some(e => e.startsWith('ZYKLUS_'));
  // Stored mode wins unless it conflicts with the values (spec §6.3); a
  // conflicting row (possible after a partner education_levels-only write)
  // is treated exactly like a legacy row.
  const storedModeIsConsistent =
    (job.levelMode === 'ZYKLEN' && allZyklus) ||
    (job.levelMode === 'STUFEN' && !anyZyklus);
  const isLegacy = !storedModeIsConsistent;
  const mode: LevelModeEnum = isLegacy
    ? deriveLegacyMode(levels)
    : job.levelMode!;
  const grades = job.grades ?? [];
  const specs = (job.schoolLevelDescriptions ?? [])
    .map(d => REVERSE_SCHOOL_LEVEL_MAP[d])
    .filter(Boolean) as SchoolLevelEnum[];
  const s: EditorState = {
    ...emptyEditorState,
    mode,
    sek1Models: job.sek1Models ?? [],
  };

  if (mode === 'ZYKLEN') {
    s.zyklen = ZYKLEN.filter(z => enums.includes(z.level)).map(z => z.key);
    s.zyklusGrades = sortGrades(grades);
    return s;
  }
  // Stufen: legacy Zyklus/Basisstufe entries are converted to explicit grades.
  const extraGrades: GradeEnum[] = [];
  for (const e of enums) {
    switch (e) {
      case 'KINDERGARTEN':
        s.kiga = true;
        break;
      case 'BASISSTUFE':
        s.kiga = true;
        s.primar = true;
        extraGrades.push(...LEGACY_LEVEL_GRADES.BASISSTUFE!);
        break;
      case 'UNTERSTUFE':
      case 'MITTELSTUFE':
      case 'PRIMARSTUFE':
        s.primar = true;
        if (e !== 'PRIMARSTUFE' && isLegacy)
          extraGrades.push(...LEGACY_LEVEL_GRADES[e]!);
        break;
      case 'SEKUNDARSTUFE_I':
        s.sek1 = true;
        break;
      case 'SEKUNDARSTUFE_II':
        s.sek2 = true;
        break;
      case 'TERTIAERSTUFE':
        s.tertiaer = true;
        break;
      case 'QUARTAERSTUFE':
        s.quartaer = true;
        break;
      case 'ZYKLUS_I':
        s.kiga = true;
        s.primar = true;
        extraGrades.push(...zyklusByKey('I').grades);
        break;
      case 'ZYKLUS_II':
        s.primar = true;
        extraGrades.push(...zyklusByKey('II').grades);
        break;
      case 'ZYKLUS_III':
        s.sek1 = true;
        extraGrades.push(...zyklusByKey('III').grades);
        break;
    }
  }
  const all = [...grades, ...extraGrades];
  if (s.kiga) s.kigaGrades = within(all, KIGA_GRADES);
  if (s.primar) s.primarGrades = within(all, gradesInRange(1, 6));
  if (s.sek1) s.sek1Grades = within(all, gradesInRange(7, 9));
  s.sek2Specs = specsIn(specs, SEK2_SPECS);
  s.tertiaerSpecs = specsIn(specs, TERTIAER_SPECS);
  s.quartaerSpecs = specsIn(specs, QUARTAER_SPECS);
  return s;
}

export type JobLevelPatch = Required<
  Pick<
    Job,
    | 'levelMode'
    | 'schoolLevels'
    | 'schoolLevelDescriptions'
    | 'grades'
    | 'sek1Models'
  >
>;

export function editorStateToJobPatch(s: EditorState): JobLevelPatch {
  const label = (k: SchoolLevelEnum) => SCHOOL_LEVEL_MAP[k] as JobLevelSpec;
  if (s.mode === 'ZYKLEN') {
    const range = s.zyklen.map(zyklusByKey).flatMap(z => z.grades);
    return {
      levelMode: 'ZYKLEN',
      schoolLevels: ZYKLEN.filter(z => s.zyklen.includes(z.key)).map(
        z => z.label as JobLevel
      ),
      schoolLevelDescriptions: [],
      grades: within(s.zyklusGrades, range),
      sek1Models: s.zyklen.includes('III') ? s.sek1Models : [],
    };
  }
  const levels: JobLevel[] = [];
  const grades: GradeEnum[] = [];
  const specs: JobLevelSpec[] = [];
  if (s.kiga) {
    levels.push('Kindergarten');
    grades.push(...within(s.kigaGrades, KIGA_GRADES));
  }
  if (s.primar) {
    const g = within(s.primarGrades, gradesInRange(1, 6));
    const n = g.map(gradeNumber);
    levels.push(
      n.length && n.every(x => x <= 3)
        ? 'Unterstufe'
        : n.length && n.every(x => x >= 4)
          ? 'Mittelstufe'
          : 'Primarstufe'
    );
    grades.push(...g);
  }
  if (s.sek1) {
    levels.push('Sekundarstufe I');
    grades.push(...within(s.sek1Grades, gradesInRange(7, 9)));
  }
  if (s.sek2) {
    levels.push('Sekundarstufe II');
    specs.push(...specsIn(s.sek2Specs, SEK2_SPECS).map(label));
  }
  if (s.tertiaer) {
    levels.push('Tertiärstufe');
    specs.push(...specsIn(s.tertiaerSpecs, TERTIAER_SPECS).map(label));
  }
  if (s.quartaer) {
    levels.push('Quartärstufe');
    specs.push(...specsIn(s.quartaerSpecs, QUARTAER_SPECS).map(label));
  }
  return {
    levelMode: 'STUFEN',
    schoolLevels: levels,
    schoolLevelDescriptions: specs,
    grades: sortGrades(grades),
    sek1Models: s.sek1 ? s.sek1Models : [],
  };
}
